import { Button, Col, Form, Row, Select, Space, Typography } from "antd";
import { Icon } from "@iconify/react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ContentWrapper } from "../../../Components/Layout/ContentWrapper";
import { useQuery } from "../../../Hooks/useQuery";
import { approverApi, toArray } from "../../../Services";
import { usePanelOrgTree } from "../../../Hooks/usePanelOrgTree";
import { useUserDirectory } from "../../../Hooks/useUserDirectory";
import { useNotification } from "../../../Hooks/useNotification";

import { APPROVER_GROUPS } from "../Constants";
import { SuccessModal } from "../SuccessModal";
import styles from "../ApprovalManagement.module.scss";
import workOrderStyles from "../../WorkOrder/WorkOrder.module.scss";

interface ApproverDraft {
  key: string;
  designation: string;
  subsidiary: string;
  costCentre: string;
  userName: string;
  userEmail: string;
}

const blankRow = (seed: number): ApproverDraft => ({
  key: `approver-${seed}`,
  designation: "",
  subsidiary: "",
  costCentre: "",
  userName: "",
  userEmail: "",
});

/**
 * Create / edit approvers for one designation group.
 *
 * The group comes in as `?group=` and titles the section, e.g.
 * "Approvers Details ( MD/GCEO)" — the Designation options are the ones that
 * group allows, so an APC designation can't be saved under MD/GCEO.
 *
 * Rows are repeatable; Cost Centre cascades off Subsidiary/Division and
 * User Name / User Email stay linked, same as the admin rows elsewhere.
 * `?id=` switches the screen to edit.
 */
export const CreateApprover = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const id = query.get("id");
  const groupKey = query.get("group") || APPROVER_GROUPS[0].key;
  const group = APPROVER_GROUPS.find((entry) => entry.key === groupKey) ?? APPROVER_GROUPS[0];

  const [rows, setRows] = useState<ApproverDraft[]>([blankRow(0)]);
  const [successOpen, setSuccessOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  /* Name / Email options. Approvers are picked from the active users, not the
     admin-only list the subsidiary and cost centre forms use. */
  const { users: adminDirectory, byName, byEmail } = useUserDirectory("active");
  /* Subsidiary and Cost Centre from the one configured source the rest of the
     panel reads, rather than this screen fetching its own — the stored
     approver holds NAMES (`subsidiary: "MASB"`, `division: "PENANG - AFRS"`),
     so names are what the pickers offer and submit. */
  const { subsidiaries, costCentresFor, resolveSubsidiary } = usePanelOrgTree();
  const { openNotification } = useNotification();

  /* Edit mode: hydrate the single row being edited. The API's `division` is
     this UI's Cost Centre. */
  useEffect(() => {
    if (!id) return;
    approverApi
      .detail(id)
      .then((record: any) => {
        if (!record) return;
        /* Records saved before this was fixed hold the id here; resolve it to
           the name so the picker matches and the cost centres line up. */
        const subsidiary = resolveSubsidiary(record.subsidiary);

        setRows([
          {
            key: "approver-0",
            designation: record.designation ?? "",
            subsidiary,
            costCentre: record.division ?? "",
            userName: record.userName ?? "",
            userEmail: record.userEmail ?? "",
          },
        ]);
      })
      .catch(() => undefined);
    // eslint-disable-next-line
  }, [id, resolveSubsidiary]);

  const handleSubmit = () => {
    setIsSaving(true);

    /* Update takes a single object and needs the uniqueId; create takes an
       array. Editing previously fell through to create, which silently added
       a duplicate instead of updating the record. */
    if (id) {
      const row = rows[0];
      approverApi
        .update({
          uniqueId: id,
          userName: row.userName,
          userEmail: row.userEmail,
          division: row.costCentre,
          designation: row.designation,
          subsidiary: row.subsidiary,
          authorityGroup: group.authorityGroup,
        })
        .then(() => setSuccessOpen(true))
        .catch((err: any) =>
          openNotification({ title: "Failed to update approver", message: err?.message, type: "error" })
        )
        .finally(() => setIsSaving(false));
      return;
    }

    approverApi
      .create(
        rows.map((row) => ({
          userName: row.userName,
          userEmail: row.userEmail,
          division: row.costCentre,
          designation: row.designation,
          subsidiary: row.subsidiary,
          authorityGroup: group.authorityGroup,
        }))
      )
      .then(() => setSuccessOpen(true))
      .catch((err: any) =>
        openNotification({ title: "Failed to save approvers", message: err?.message, type: "error" })
      )
      .finally(() => setIsSaving(false));
  };

  const backTo = `/approval-management?activeTab=approvers&group=${group.key}`;

  const update = (key: string, patch: Partial<ApproverDraft>) =>
    setRows(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  /**
   * Duplicate detection.
   *
   * A user may hold several designations (R1, R2, MD…) in the same subsidiary
   * and cost centre; what is a duplicate is the same user with the same
   * designation in the same subsidiary and cost centre.
   *
   * Submitting a repeat used to reach the server, which rejected the batch
   * with 400 while having already written part of it — so the error said the
   * approver existed and the listing showed it anyway.
   */
  const identityOf = (designation: any, subsidiary: any, costCentre: any, email: any) =>
    [designation, subsidiary, costCentre, email]
      .map((part) => String(part ?? "").trim().toLowerCase())
      .join("|");

  /* Already configured in this group. The record being edited is excluded, so
     re-saving it unchanged is not treated as a clash with itself. */
  const [configured, setConfigured] = useState<any[]>([]);
  useEffect(() => {
    approverApi
      .list({ authorityGroup: group.authorityGroup, page: 0, size: 1000 })
      .then((result: any) =>
        setConfigured(toArray(result).filter((record: any) => record.uniqueId !== id))
      )
      .catch(() => setConfigured([]));
  }, [group.authorityGroup, id]);

  const configuredIdentities = useMemo(
    () =>
      new Set(
        configured.map((record: any) =>
          identityOf(record.designation, resolveSubsidiary(record.subsidiary), record.division, record.userEmail)
        )
      ),
    [configured, resolveSubsidiary]
  );

  /** row key -> why it is a duplicate. */
  const duplicates = useMemo(() => {
    const seen = new Set<string>();
    const flagged = new Map<string, string>();

    rows.forEach((row) => {
      if (!row.designation || !row.subsidiary || !row.costCentre || !row.userEmail) return;
      const identity = identityOf(row.designation, row.subsidiary, row.costCentre, row.userEmail);

      if (configuredIdentities.has(identity)) {
        flagged.set(
          row.key,
          "This user already has this designation for that subsidiary and cost centre."
        );
      } else if (seen.has(identity)) {
        flagged.set(row.key, "Same user, designation, subsidiary and cost centre as an earlier row.");
      } else {
        seen.add(identity);
      }
    });

    return flagged;
  }, [rows, configuredIdentities]);

  const isValid =
    rows.length > 0 &&
    duplicates.size === 0 &&
    rows.every(
      (row) => row.designation && row.subsidiary && row.costCentre && row.userName && row.userEmail
    );

  return (
    <ContentWrapper
      customTitle={
        <Space align="start">
          <Icon
            onClick={() => navigate(backTo)}
            icon="akar-icons:arrow-left"
            className={workOrderStyles.backIcon}
          />
          <Typography.Title
            level={3}
            style={{ margin: 0 }}
          >
            {id ? "Edit Approver" : "Create Approver"}
          </Typography.Title>
        </Space>
      }
    >
      <Form layout="vertical">
        <Typography.Title level={4}>Approvers Details ( {group.label})</Typography.Title>

        {rows.map((row) => (
          <Row
            gutter={16}
            key={row.key}
            align="top"
          >
            <Col
              xs={24}
              sm={12}
              lg={4}
            >
              <Form.Item required label="Designation">
                <Select
                  showSearch
                  optionFilterProp="children"
                  value={row.designation || undefined}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                  onChange={(value: string) => update(row.key, { designation: value })}
                >
                  {group.designations.map((designation: string, index: number) => (
                    <Select.Option
                      value={designation}
                      key={`${index}`}
                    >
                      {designation}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={12}
              lg={5}
            >
              <Form.Item required label="Subsidiary/Division">
                <Select
                  showSearch
                  optionFilterProp="children"
                  value={row.subsidiary || undefined}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                  onChange={(value: string) => {
                    // Clear the cost centre: it belongs to the previous subsidiary.
                    update(row.key, { subsidiary: value, costCentre: "" });
                  }}
                >
                  {subsidiaries.map((name: string, index: number) => (
                    <Select.Option
                      value={name}
                      key={`${index}`}
                    >
                      {name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={12}
              lg={5}
            >
              <Form.Item required label="Cost Centre">
                <Select
                  showSearch
                  optionFilterProp="children"
                  value={row.costCentre || undefined}
                  disabled={!row.subsidiary}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                  onChange={(value: string) => update(row.key, { costCentre: value })}
                >
                  {costCentresFor([row.subsidiary].filter(Boolean)).map((name: string, index: number) => (
                    <Select.Option
                      value={name}
                      key={`${index}`}
                    >
                      {name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={12}
              lg={4}
            >
              <Form.Item required label="User Name">
                <Select
                  showSearch
                  optionFilterProp="children"
                  value={row.userName || undefined}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                  onChange={(value: string) => {
                    const match = adminDirectory.find((entry) => entry.name === value);
                    update(row.key, { userName: value, userEmail: match?.email ?? row.userEmail });
                  }}
                >
                  {byName.map((entry, index) => (
                    <Select.Option
                      value={entry.name}
                      key={`${index}`}
                    >
                      {entry.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={12}
              lg={5}
            >
              <Form.Item required
                label="User Email"
                /* The duplicate is surfaced on the field that identifies the
                   approver, so the offending row is obvious in a long list. */
                validateStatus={duplicates.has(row.key) ? "error" : undefined}
                help={duplicates.get(row.key)}
              >
                <Select
                  showSearch
                  optionFilterProp="children"
                  value={row.userEmail || undefined}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                  onChange={(value: string) => {
                    const match = adminDirectory.find((entry) => entry.email === value);
                    update(row.key, { userEmail: value, userName: match?.name ?? row.userName });
                  }}
                >
                  {byEmail.map((entry, index) => (
                    <Select.Option
                      value={entry.email}
                      key={`${index}`}
                    >
                      {entry.email}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {!id && (
              <Col>
                <Icon
                  icon="fluent:delete-48-regular"
                  className={styles.adminRowDelete}
                  onClick={() =>
                    setRows(
                      rows.length === 1 ? [blankRow(0)] : rows.filter((entry) => entry.key !== row.key)
                    )
                  }
                />
              </Col>
            )}
          </Row>
        ))}

        {/* One record at a time when editing — the update endpoint takes a
            single object, so extra rows would have nowhere to go. */}
        {!id && (
          <Row justify="end">
            <Col>
              <span
                className={styles.addAnother}
                onClick={() => setRows([...rows, blankRow(rows.length + 1)])}
              >
                <Icon
                  icon="akar-icons:plus"
                  inline
                />
                Add Another
              </span>
            </Col>
          </Row>
        )}

        <Row
          justify="end"
          gutter={16}
          style={{ marginTop: 32 }}
        >
          <Col>
            <Button onClick={() => navigate(backTo)}>Cancel</Button>
          </Col>
          <Col>
            <Button
              type="primary"
              disabled={!isValid}
              loading={isSaving}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </Col>
        </Row>
      </Form>

      <SuccessModal
        open={successOpen}
        message={id ? "Approver updated successfully" : "Approvers are added successfully"}
        onClose={() => {
          setSuccessOpen(false);
          navigate(backTo);
        }}
      />
    </ContentWrapper>
  );
};
